import { z } from "zod";

import type { TSkillScriptRunRecord } from "../types/skill-sandbox";
import { skillScriptRunRecordSchema } from "../types/skill-sandbox";
import type {
  IMergeSkillScriptRunsProps,
  IMessageWithSkillScriptRuns,
  IReadSkillScriptRunsProps,
  IResolveSkillScriptRunInputNamesProps,
  ISetMessageSkillScriptRunsProps,
} from "./skill-script-runs.types";

// NOTE: NSAI-3304 — ONE implementation of the run bookkeeping, shared by web and
// mobile. Both clients receive the same records over the same socket event and the
// same history payload, so a per-app copy of this logic is a per-app chance for the
// transcripts to disagree about what a run did.
const skillScriptRunCarrierSchema = z.object({
  // NOTE: zod 4 requires a `z.unknown()` key unless it is marked optional.
  skillScriptRuns: z.unknown().optional(),
});

// NOTE: The history read is the durable source for a run, and it is parsed rather
// than trusted. A record that does not match the shared contract is dropped instead
// of reaching the transcript: the payload carries a script's own stdout and file
// names, so a malformed entry is exactly the input that must not be able to take the
// message list down.
const parseSkillScriptRunList = (value: unknown): TSkillScriptRunRecord[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const records: TSkillScriptRunRecord[] = [];
  for (const entry of value) {
    const parsed = skillScriptRunRecordSchema.safeParse(entry);
    if (parsed.success) {
      records.push(parsed.data);
    }
  }
  return records;
};

// NOTE: Ordering comes from the server's `position`, never from arrival order, so
// the same message reads the same way live and after a refresh.
const sortSkillScriptRuns = (
  runs: readonly TSkillScriptRunRecord[],
): TSkillScriptRunRecord[] =>
  [...runs].sort((left, right) => left.position - right.position);

export const readSkillScriptRuns = ({
  message,
}: IReadSkillScriptRunsProps): TSkillScriptRunRecord[] => {
  const carrier = skillScriptRunCarrierSchema.safeParse(message);

  return sortSkillScriptRuns(
    parseSkillScriptRunList(
      carrier.success ? carrier.data.skillScriptRuns : undefined,
    ),
  );
};

// NOTE: MERGED by run `uuid`, never appended. That is what makes the transcript show
// ONE card per run in every replay case: a progress frame followed by a terminal
// frame, a socket event redelivered after a reconnect, and a history refetch that
// arrives after the live event. A later record for the same run wins, so the terminal
// state replaces the streamed `running` copy rather than appending a twin — a run
// only ever moves forward (queued -> running -> final).
export const mergeSkillScriptRuns = ({
  existing,
  incoming,
}: IMergeSkillScriptRunsProps): TSkillScriptRunRecord[] => {
  const byRunUuid = new Map<string, TSkillScriptRunRecord>();
  for (const record of existing ?? []) {
    byRunUuid.set(record.uuid, record);
  }
  for (const record of incoming) {
    byRunUuid.set(record.uuid, record);
  }

  return sortSkillScriptRuns([...byRunUuid.values()]);
};

export const setMessageSkillScriptRuns = ({
  message,
  runs,
}: ISetMessageSkillScriptRunsProps): void => {
  const carrier: IMessageWithSkillScriptRuns = message;
  carrier.skillScriptRuns = runs;
};

// NOTE: NSAI-6662 — the attachments a run's script was actually given, for the
// "Input files" line web and mobile both render. `null` means "say nothing":
// the script never ran (no exit code yet — still going, or refused, cancelled
// or failed before its container started, so it received nothing), or the
// record came from a server that does not report inputs. An empty array is the
// positive statement that a script that ran got no files — the case where a
// clean exit over zero rows otherwise looked like a successful audit of an upload.
export const resolveSkillScriptRunInputNames = ({
  run,
}: IResolveSkillScriptRunInputNamesProps): string[] | null => {
  if (run.exitCode === null || run.inputFiles === undefined) {
    return null;
  }
  return run.inputFiles.map((file) => file.name);
};
